/**
 * AsConfigurator Web Component
 *
 * Manages the Paramate configurator instance and coordinates communication between
 * the configurator API and other components in the application.
 *
 * Key Responsibilities:
 * - Initializes and manages the Paramate configurator instance
 * - Handles asynchronous initialization with operation queuing
 * - Provides mesh generation and export functionality
 * - Manages file uploads to the configurator
 * - Exposes configurator parameters and session info
 *
 * Events Dispatched:
 * - 'as-intrn-configurator-ready': Fired once, when the configurator is bound and usable
 * - 'as-intrn-system-info': Response to 'as-intrn-get-system-info' requests
 *
 * Events Listened For:
 * - 'as-app': Get reference to parent app component
 * - 'as-intrn-get-system-info': Request for session info
 * - 'as-get-configurator-mesh': Request for mesh blob
 * - 'as-intrn-get-mesh-url': Request for mesh URL
 * - 'as-intrn-upload-file': Request to upload file to configurator
 * - 'as-configure': Request to configure with params
 *
 * Usage:
 * <as-configurator></as-configurator>
 * await asConfigurator().init({ ... })
 *
 * init() is the only way the component gets its Paramate.Configurator: it
 * builds the instance, awaits the SDK's initialize() and binds straight after.
 * It does not listen for the SDK's 'paramate-configurator-initialized' event.
 *
 * Session reinits (session-expiry restore, New Project, etc.) happen IN PLACE
 * on the same instance (_reinitSession), so caching a reference to
 * `.configurator` is safe: the object identity survives every reinit. The SDK
 * still rotates token/session/params in separate async stages during
 * initialize(); the hardening around that lives in _reinitSession (call gate,
 * idle wait, rollback snapshot) and _isStaleParamateEvent (generation
 * counter). See docs/as-configurator.md#session-reinitialization.
 *
 * Which configuratorDetails tier init() uses is decided in
 * components/paramate/lib/configuratorTier.ts. On localhost: a ?server=local|dev|staging|
 * production URL param forces a tier; the `local-server` attribute forces
 * local; otherwise local is used when its APIHub responds to a probe, and
 * dev when it doesn't. The winning tier is written back to the URL as
 * ?server=... so the active choice is always visible.
 * <as-configurator local-server ...></as-configurator>
 */

import { devlog } from "../../util/utils.ts";
import { unzipSync, strFromU8 } from 'fflate';
import { currentProjectData } from "../../data/projectManagement.ts";
import * as History from "../../features/history/undoHistory.ts";
import * as ParamateUndo from "./lib/paramateUndoManager.ts";
import { normalizeTokenKey } from "./lib/paramateUndoManager.ts";
import { AsApp, type GetMeshEvent, getAppConfig, type ParamateDetails } from "../as-app/index.ts";
import { DEFAULT_PARAMATE_API_HUB, isOnPrem } from "../../data/constants.ts";
import { internalData } from "../../store/store.ts";
import {
  getRegisteredParamNames,
  resolveParamPending,
  hasPendingConfigureValues,
  configurePending,
} from "../../store/paramInputRegistry.ts";
import { getPendingParams, pendingParamChanges } from "./lib/paramatePending.ts";
import { params, publishParams, lookupParam, type ParamData } from "../../store/paramStore.ts";
import { _setSysData } from "../../store/sysDataStore.ts";
import { AsViewport } from "../viewport/as-viewport.ts";
import type { AsFileHandle } from "../../features/importExport/import/fileBase.ts";
import { wcQry } from "../../util/wcUtil.ts";
import { _registerSingleton, _unregisterSingleton, asApp } from '../../util/singletons.ts';
import { resolveConfiguratorDetails } from "./lib/configuratorTier.ts";
import { startSlowOpNotice, cancelMessageDelayMs, type SlowOpNotice } from "../../util/slowOpNotice.ts";
import { AsEl } from "../base.ts";
import { classifyParamateError, installParamateConstructorPatch } from "./paramateErrorInterceptor.ts";
import {
  handleParamInputChange,
  syncParamValuesToInputs,
  readParamValues,
} from "./configuratorParams.ts";
import { sendEv } from '../../data/events.ts';

// ============================================================================
// TYPES
// ============================================================================

export interface AsConfiguratorInitOptions {
  /**
   * Configurator credentials per environment. If omitted, resolved automatically
   * from data/app.cfg.json (configuratorDetails key) via getAppConfig, with a
   * further fallback to the app-suite registry (appInfo[appId].configurator).
   */
  configuratorDetails?: {
    production?: ParamateDetails;
    staging?: ParamateDetails;
    dev?: ParamateDetails;
    local?: ParamateDetails;
  };
  /** Override the viewport element. If omitted, searches for <as-viewport>. */
  viewportElement?: AsViewport | null;
  /** Extra options forwarded to `new Paramate.ViewportWebGL({ configurator, viewportElement, ...viewportOptions })`
   *  (e.g. postProcessAA, webGLAA, cameraAutoPos). Only used when a viewport element is found.
   *  `useWaitAnim` defaults to false, because the library puts a spinner in
   *  `#as-main-content` for the same state — set it here to force either way.
   *  It is true when `<as-app no-spinner>` means there is no app spinner. */
  viewportOptions?: Record<string, unknown>;
  // Paramate pass-through options
  proxy?: string;
  controls?: any;
  onBeforeConfigure?: Function;
  onAfterConfigure?: Function;
  onAfterConfigureRedraw?: Function;
  onConfigureError?: Function;
  undoLevel?: number;
  /**
   * Legacy escape hatch. The Paramate.Configurator constructor is patched
   * globally (see paramateErrorInterceptor.ts) to suppress the SDK's own
   * alert()-based debug.error and always classify/show errors via
   * as-configurator — passing this is no longer needed to prevent that alert.
   * Only set it for a raw, low-level side-effect hook independent of the
   * classification system; prefer listening for the `as-paramate-error`
   * window event (carries a `classification` field) instead.
   */
  debug?: { on?: boolean; log?: Function; error?: Function };
}

/**
 * Behaviour switches, readable and settable at any time through
 * `asConfiguratorEl.options`. Assigning merges, so setting one switch leaves
 * the rest alone: `el.options = { pendingParams: false }`.
 */
export interface AsConfiguratorOptions {
  /**
   * Capture params set with `setParamValue(..., false)` — client-side edits no
   * configure has sent to the server — into the saved project, and replay them
   * onto the session after a restore. On by default.
   *
   * Turn it off in an app that rebuilds that state itself on load. The replay
   * runs after migrations, so a replayed value wins over anything a migration
   * wrote for the same key; an app whose migration refills those params is
   * better off not carrying them at all. For a conflict over a few keys rather
   * than the whole mechanism, edit the `pending` map inside the session
   * migration instead (see SessionTransform) and leave this on.
   */
  pendingParams?: boolean;
}

interface QueuedOperation {
  type: string;
  execute: (configurator: any) => Promise<void>;
}

/** What the upload path takes as a file: a real `File`, or an `AsFileHandle`
 *  (unwrapped via `.toFile()` before it reaches Paramate, which needs
 *  `file.name` for the upload's filename). */
export type UploadFileInput = File | AsFileHandle;

interface QueuedUpload extends QueuedOperation {
  type: 'upload';
  file: UploadFileInput | UploadFileInput[];
  fileParamName: string;
}

/** The options object Paramate's own `configurator.uploadFile()` takes.
 *  `<as-configurator>`'s uploadFile accepts it too, so a raw configurator call
 *  can move to the wrapper unchanged. */
export interface ConfiguratorUploadOptions {
  file: UploadFileInput | UploadFileInput[];
  /** Slot to overwrite (PUT). Leave it out and the server allocates one (POST). */
  fileId?: string;
  progress?: Function;
  success?: (uploadRes: any) => void;
}

/** Only the options object has a `file` property — a File or AsFileHandle doesn't. */
function isUploadOptions(arg: any): arg is ConfiguratorUploadOptions {
  return !!arg && typeof arg === 'object' && !Array.isArray(arg) && !(arg instanceof Blob) && 'file' in arg;
}

/** Trailing options for the calls that can push an undo entry. Sits after the
 *  callback so the arguments before it match stock Paramate exactly. */
export interface ParamateHistoryOptions {
  /** Don't add the resulting configuration to the undo stack. */
  skipHistory?: boolean;
}

/** These methods used to take `skipHistory` where the callback now sits. A
 *  boolean can never be a callback, so read it as the old flag and keep the
 *  call working — but say so once per method, since the value would otherwise
 *  be dropped silently. */
const _legacySkipHistoryWarned = new Set<string>();
function readCallbackArg(method: string, arg: Function | boolean | undefined, newForm: string): { callback?: Function; skipHistory: boolean } {
  if (typeof arg !== 'boolean') return { callback: arg, skipHistory: false };
  if (!_legacySkipHistoryWarned.has(method)) {
    _legacySkipHistoryWarned.add(method);
    console.warn(`[as-configurator] ${method}() now takes a callback in that position, matching stock Paramate. The boolean you passed is being read as the old skipHistory flag — use ${newForm} instead.`);
  }
  return { skipHistory: arg };
}

/** Clears the once-per-method latch above so a test can assert the warning
 *  without depending on which test ran first. */
export function resetLegacySignatureWarningsForTests(): void {
  _legacySkipHistoryWarned.clear();
}

// ============================================================================
// COMPONENT DEFINITION
// ============================================================================

function minutesToMs(mins:number){return mins*60*1000}

/** How long _reinitSession waits for in-flight SDK requests to settle before
 *  rotating the session. Best effort — after this the rotation proceeds
 *  regardless (the generation guard drops whatever still lands late). */
const REINIT_IDLE_WAIT_MS = 5000;

/** How long a busy waits for the viewport to show the model it holds for
 *  (cfgr-init after initialize(), configure after after-configure) before it
 *  is released anyway. A redraw cancelled by onBeforeRedraw or lost to a
 *  render error never dispatches its event. */
const REDRAW_FALLBACK_MS = 15000;

/** Re-exported for existing callers that import these from as-configurator.ts —
 *  the implementations now live in paramateUndoManager.ts alongside the rest
 *  of the undo/redo machinery that uses them. */
export { cloneParamValue, paramValuesEqual } from "./lib/paramateUndoManager.ts";

/** Marks an SDK setParamValue that _publishStagedWrites has already wrapped. */
const STAGED_WRITE_PUBLISHES = Symbol('as-staged-write-publishes');

export class AsConfigurator extends AsEl {
  static readonly tag = 'as-configurator' as const;
  /** The live Paramate Configurator instance, created by init(). A session
   *  reinit (_reinitSession: session-expiry restore, New Project, etc.)
   *  rotates the server session on this same object rather than replacing it,
   *  so caching a reference to it is safe: the identity survives every reinit. */
  public configurator: any;

  public initialized: boolean = false;

  /** Behaviour switches — see AsConfiguratorOptions. Assignment merges rather
  *  than replaces, so an app can set one switch without knowing the others. */
  get options(): AsConfiguratorOptions { return this._options; }
  set options(v: AsConfiguratorOptions) { this._options = { ...this._options, ...v }; }
  private _options: AsConfiguratorOptions = { pendingParams: true };

  private _waitingForSysInfo: Array<HTMLElement> = [];
  private _configuratorReadyResolvers: Array<(configurator: any) => void> = [];
  private _app: AsApp;
  private _operationQueue: QueuedOperation[] = [];
 
  private _spinner: HTMLElement | null = null;
  private _sessionTimeoutMax = minutesToMs(13);
  private _intervalInstance = null
  private _configureNotice: SlowOpNotice | null = null;
  /** True while a saveSession()/getSessionBlob() round-trip is in flight — lets
   *  handleParamateError distinguish a save-timeout (row 5) from a general one (row 6). */
  private _saveInFlight = false;
  private _restoreInFlightFlag = false;
  /** True while restoreSession() is in flight — its own onFail owns the resulting
   *  error UI (session-file-not-recognized), so handleParamateError must not also
   *  pop a competing modal for the same underlying doRequest() failure.
   *
   *  An accessor pair rather than a plain field so the mirror into
   *  sysData.sessionRestoring happens in one place — restoreSession() clears it
   *  down two different paths (its own onFail, and the returned promise), and a
   *  third would be easy to add and easy to forget. */
  private get _restoreInFlight(): boolean { return this._restoreInFlightFlag; }
  private set _restoreInFlight(value: boolean) {
    this._restoreInFlightFlag = value;
    _setSysData({ sessionRestoring: value });
  }

  // Undo/redo state and the upload-token lifecycle it drives now live in
  // paramateUndoManager.ts (a singleton module, like undoHistory.ts) —
  // see docs/as-configurator.md#what-this-component-manages.
  /** Completion promise of the in-flight reinit (initialize()); non-null while one is
   *  running. Collapses the as-reset-configurator + as-after-project-new double-fire into a
   *  single initialize() — overlapping callers all await the same promise. */
  private _reinitPromise: Promise<void> | null = null;
  /** In-flight liveness probe from _confirmSessionLost(). Non-null means a
   *  401/404 is being verified against the bound session before the expiry
   *  flow is allowed to start. */
  private _sessionLostProbe: Promise<void> | null = null;
  /** True while a keepalive request is outstanding. Its own catch decides
   *  whether the session is lost, so handleParamateError doesn't probe for a
   *  401/404 that arrives in that window (see sessionHeartbeat). */
  private _heartbeatInFlight = false;
  /** Monotonic generation of the bound server session. Bumped on every bind
   *  and at the start of every reinit. Because a reinit keeps the instance,
   *  object identity can no longer tell a straggler configure cycle (started
   *  against the pre-reinit session) from a live one — the generation can:
   *  before-configure stamps the generation it opened under (_configureGen)
   *  and after-configure compares. See _isStaleParamateEvent. */
  private _cfgrGeneration = 0;
  /** Generation the most recent (non-stale) before-configure was stamped with. */
  private _configureGen = 0;

  /** True while a session reinit is in flight (see _reinitSession). Public so
   *  hosts can gate their own side effects on the session swap. */
  get isReinitializing(): boolean {
    return this._reinitPromise !== null;
  }

  static get observedAttributes() {
    return [...super.observedAttributes, 'configurator-id', 'api-key', 'pause-on-session-timeout', 'seconds-before-cancel-message', 'max-history', 'default-export-type'];
  }

  /** File type to request from the configurator when exporting mesh parts (default: '3mf'). */
  get defaultExportFileType(): string {
    return this.getAttribute('default-export-type') ?? '3mf';
  }

  set defaultExportFileType(value: string) {
    this.setAttribute('default-export-type', value);
  }

  setDefaultMeshType(value: string): void {
    this.defaultExportFileType = value;
  }

  attributeChangedCallback(name: string, _old: string | null, _new: string | null) {
    super.attributeChangedCallback(name, _old, _new);
    if (name === 'max-history') {
      History.setMaxHistory(parseInt(this.getAttribute('max-history') ?? '50', 10) || 50);
    }
  }

  // ============================================================================
  // INITIALIZATION
  // ============================================================================

  constructor() {
    super();
    this.configurator = null;
    this.initialized = false;
    this._waitingForSysInfo = [];
    this._operationQueue = [];
    this._spinner = null;
    // paramateUndoManager.ts is a singleton module (see its file header) — point
    // it at this instance's live configurator. A callback, not a snapshot, since
    // the configurator does not exist until init() runs.
    ParamateUndo.bindConfigurator(() => this.configurator);
  }
  /** A 401/404 landed and no reinit is in flight — but that alone doesn't
   *  prove the BOUND session expired. A reinit leaves the abandoned session's
   *  stragglers in the air, and the SDK dispatches paramate-configurator-error
   *  for every failed request regardless of which session it was for. Taking
   *  those at face value pops the expired-session modal and offers to restore
   *  the OLD project into the brand-new session.
   *
   *  So instead of trusting the error, check whether the session we are
   *  actually bound to still answers. Only if it doesn't is the session
   *  genuinely lost. Costs one request, on an error path, for auth/not-found
   *  errors only. (Ported from the 0.17 branch, where the same straggler
   *  misattribution was found by its reinit stress suite.) */
  private _confirmSessionLost(err: any): Promise<void> {
    if (this._sessionLostProbe) return this._sessionLostProbe;
    const probed = this.configurator?.configSession;
    this._sessionLostProbe = Promise.resolve(this.configurator?.getObjectInfo())
      .then(
        () => {
          devlog('[session] 401/404 was a straggler — the bound session still answers, ignoring:', err);
        },
        (probeErr: any) => {
          if (probeErr?.status !== 404 && probeErr?.status !== 401) {
            // Network blip or 5xx while probing: no evidence either way, so
            // don't tear the session down on it.
            devlog('[session] Session liveness probe failed transiently — assuming alive:', probeErr);
            return;
          }
          if (this._reinitPromise || this.configurator?.configSession !== probed) {
            // A reinit started (or finished) while we were probing, so the
            // session we tested is no longer the one that matters.
            devlog('[session] Session rotated during liveness probe — ignoring:', probeErr);
            return;
          }
          this._declareSessionLost(err);
        },
      )
      .finally(() => { this._sessionLostProbe = null; });
    return this._sessionLostProbe;
  }

  /** The bound session is gone for sure: stop pinging it and hand off to the
   *  expiry flow (as-app listens for as-session-lost). */
  private _declareSessionLost(err: any): void {
    devlog('[session] Session confirmed lost:', err);
    this.pauseSessionHeartbeat();
    sendEv(window, 'as-session-lost', { detail: { error: err } });
  }

  private _sessionHeartbeat = () => {
    devlog("[session] Session keepalive")
    // A keepalive against a dead session (e.g. the machine slept past the session
    // TTL with the tab visible, so pauseSessionHeartbeat never ran and the overdue
    // timer fires on resume) rejects with a 404. Never let that escape as an
    // unhandled rejection — it would pop the global error modal over the
    // expired-session flow. On a session-auth failure, stop the heartbeat and hand
    // off to the expiry flow; the heartbeat is restarted against the fresh session
    // by _sessionStarted once refreshSession()/reinit completes.
    const sentAgainst = this.configurator.configSession;
    // The SDK reports a failed request twice: the paramate-configurator-error
    // window event first, then the promise rejection. handleParamateError sees
    // this beat's 404 before the catch below does — while this flag is set it
    // leaves the verdict to us instead of probing on its own.
    this._heartbeatInFlight = true;
    const onBeatFailed = (err: any) => {
      if (err?.status === 404 || err?.status === 401) {
        // A reinit is already producing the fresh session this flow exists to get —
        // this rejection is a straggler aimed at the abandoned session, not expiry.
        if (this._reinitPromise) {
          devlog('[session] Keepalive rejected during reinit — ignoring:', err);
          return;
        }
        // The keepalive itself can be a straggler: sent against the session we
        // were on, landing after a reinit rotated it away. We know which session
        // this one went to, so no probe is needed to tell the two apart: if we
        // are still bound to it, the bound session itself just answered 404 —
        // conclusive. If not, a reinit already replaced it and the new session
        // has its own heartbeat.
        if (this.configurator.configSession !== sentAgainst) {
          devlog('[session] Keepalive was a straggler against a rotated-away session — ignoring:', err);
          return;
        }
        this._declareSessionLost(err);
      } else {
        // Transient failure (network blip, 5xx) — keep the heartbeat running.
        devlog('[session] Keepalive failed (transient):', err);
      }
    };
    Promise.resolve(this.configurator.getObjectInfo())
      .then(() => {}, onBeatFailed)
      .finally(() => { this._heartbeatInFlight = false; });
    // The Paramate session isn't the only one that can time out — the app server
    // has its own. This event is what keeps that one warm: as-app answers it with
    // a getUserData() call. Bubbles and is composed, so it reaches window.
    sendEv(this, 'as-heartbeat')
  }
  resetSessionTimout() {
    clearInterval(this._intervalInstance)
    this._intervalInstance = setInterval(this._sessionHeartbeat, this._sessionTimeoutMax)
  }
  /** Fire the keepalive immediately — what the browser does with the overdue
   *  heartbeat timer when the machine resumes from sleep. Test hook for
   *  window.asGlobals.simulateSleep. */
  triggerHeartbeat() {
    this._sessionHeartbeat();
  }
  pauseSessionHeartbeat() {
    clearInterval(this._intervalInstance)
    this._intervalInstance = null
  }
  // ============================================================================
  // WINDOW LISTENER HANDLERS
  // ============================================================================
  // Named (not inline-closure) handlers so disconnectedCallback can actually
  // remove them — removeEventListener only works with the exact function
  // reference that was added; a freshly-created closure at removal time
  // removes nothing, which is how several of these used to leak.

  private _handleAsAppReady = (ev: CustomEvent<{ sender: AsApp }>) => {
    this._app = ev.detail.sender;
  };

  private _handleGetConfiguratorMesh = async (ev: GetMeshEvent) => {
    try {
      if (!this.configurator) {
        const error = new Error("Configurator not initialized");
        error.name = "ConfiguratorMissingError";
        Object.assign(error, {
          context: "Configurator instance was not available when attempting to get mesh",
          component: "as-configurator",
          initialized: this.initialized
        });
        ev.detail.reject(error);
        return;
      }

      const finalized = ev.detail.finalized === true;
      const filename = ev.detail.filename || 'model.stl';
      const type = ev.detail.type || 'stl';

      const meshBlob = await this.getMesh(type, filename, finalized);
      ev.detail.resolve(meshBlob);
    } catch (err) {
      // Ensure the error has a proper name and context for the error panel
      if (err instanceof Error && !err.name?.includes("Error")) {
        err.name = "MeshGenerationError";
        Object.assign(err, {
          context: "Error occurred during mesh generation or fetching",
          component: "as-configurator",
          configuratorSession: this.configurator?.configSession
        });
      }
      ev.detail.reject(err);
    }
  };

  /** Handle direct mesh URL requests from export buttons */
  private _handleGetMeshUrlReq = async (ev: CustomEvent) => {
    try {
      if (!this.configurator) {
        ev.detail.reject(new Error("Configurator not initialized"));
        return;
      }

      const { type = 'stl', filename, finalized = true } = ev.detail;
      const meshURL = this.getMeshUrl(type, filename, finalized);

      if (!meshURL) {
        ev.detail.reject(new Error("Could not get mesh URL from configurator"));
        return;
      }

      ev.detail.resolve(meshURL);
    } catch (err) {
      ev.detail.reject(err);
    }
  };

  private _handleAsConfigureReq = async (ev: CustomEvent) => {
    const changes = ev.detail.params ? { ...ev.detail.params } : undefined
    const skipHistory = ev.detail.skipHistory || false;
    // configure() rejects on any doRequest failure (e.g. a 404 against a dead/expired
    // session) in addition to dispatching 'paramate-configurator-error', which
    // handleParamateError already classifies and surfaces — left unhandled here, the
    // same rejection also escapes as a raw "unhandled-rejection" over that UI.
    Promise.resolve(this.configure(changes, undefined, { skipHistory })).catch((err: any) =>
      devlog('[AS-CONFIGURATOR] as-configure failed (handled via paramate-configurator-error):', err));
  };

  // clearHistory() takes a skipRemoteDelete param — can't hand it to
  // addEventListener directly, or the Event object would land there as a
  // truthy "skip" value.
  private _handleHistoryClear = () => this.clearHistory();

  connectedCallback() {
    super.connectedCallback();
    // Claim the 'configurator' refs slot: backs asConfigurator() and resolves
    // any pending whenAsConfigurator()/whenConfigurator().
    _registerSingleton('configurator', this);
    window.addEventListener('as-app', this._handleAsAppReady);

    // Listen for paramate-specific events
    window.addEventListener('paramate-configurator-before-configure', this.handleParamateBeforeConfigure);
    window.addEventListener('paramate-configurator-after-configure', this.handleParamateAfterConfigure);
    window.addEventListener('paramate-configurator-after-restore', this.handleParamateAfterRestore);
    window.addEventListener('paramate-configurator-error', this.handleParamateError);
    window.addEventListener('as-intrn-get-system-info', this.handleSystemInfoReq);

    window.addEventListener('as-get-configurator-mesh', this._handleGetConfiguratorMesh);

    // Handle direct mesh URL requests from export buttons
    window.addEventListener('as-intrn-get-mesh-url', this._handleGetMeshUrlReq);

    window.addEventListener('as-intrn-upload-file', this._handleUploadFile);
    window.addEventListener('as-intrn-delete-file', this._handleDeleteFile);

    window.addEventListener('as-reset-configurator', this.handleResetConfigurator);
    window.addEventListener('as-after-project-new', this.handleProjectNew);

    // Update uiParams on any input change (not just after configure)
    window.addEventListener('as-input-change', this.handleInputChange);

    window.addEventListener('as-configure', this._handleAsConfigureReq);

    window.addEventListener('as-configure-pending', this.handleConfigurePending);
    window.addEventListener('as-get-session-file', this._handleGetSessionFile);
    window.addEventListener('as-undo', ParamateUndo.handleUndoRequest);
    window.addEventListener('as-undo-confirmed', ParamateUndo.handleUndoConfirmed);
    window.addEventListener('as-redo', ParamateUndo.handleRedoRequest);
    window.addEventListener('as-history-clear', this._handleHistoryClear);
    window.addEventListener('as-history-truncate-redo', ParamateUndo.handleTruncateRedoRequest);
    window.addEventListener('as-custom-data-changed', ParamateUndo.onCustomDataChanged);
    window.addEventListener('as-project-loaded', this._handleProjectLoaded);
  }

  // NOTE: must be a prototype METHOD, not a class-field arrow function — the
  // custom-elements registry caches lifecycle callbacks from the prototype at
  // customElements.define() time, so an instance-field `disconnectedCallback`
  // is never invoked by the browser and every listener below would leak.
  disconnectedCallback() {
    this._abandonConfigureWait?.();
    // Release the 'configurator' refs slot, but only if we are still in it.
    _unregisterSingleton('configurator', this);
    this._clearConfigureTimer();

    window.removeEventListener('as-app', this._handleAsAppReady);
    window.removeEventListener('as-intrn-get-system-info', this.handleSystemInfoReq);
    window.removeEventListener('as-get-configurator-mesh', this._handleGetConfiguratorMesh);
    window.removeEventListener('as-intrn-get-mesh-url', this._handleGetMeshUrlReq);
    window.removeEventListener('as-intrn-upload-file', this._handleUploadFile);
    window.removeEventListener('as-intrn-delete-file', this._handleDeleteFile);
    window.removeEventListener('as-reset-configurator', this.handleResetConfigurator);

    // Remove paramate event listeners
    window.removeEventListener('paramate-configurator-before-configure', this.handleParamateBeforeConfigure);
    window.removeEventListener('paramate-configurator-after-configure', this.handleParamateAfterConfigure);
    window.removeEventListener('paramate-configurator-after-restore', this.handleParamateAfterRestore);
    window.removeEventListener('paramate-configurator-error', this.handleParamateError);
    window.removeEventListener('as-after-project-new', this.handleProjectNew);
    window.removeEventListener('as-input-change', this.handleInputChange);
    window.removeEventListener('as-configure', this._handleAsConfigureReq);
    window.removeEventListener('as-configure-pending', this.handleConfigurePending);
    window.removeEventListener('as-get-session-file', this._handleGetSessionFile);
    window.removeEventListener('as-undo', ParamateUndo.handleUndoRequest);
    window.removeEventListener('as-undo-confirmed', ParamateUndo.handleUndoConfirmed);
    window.removeEventListener('as-redo', ParamateUndo.handleRedoRequest);
    window.removeEventListener('as-history-clear', this._handleHistoryClear);
    window.removeEventListener('as-history-truncate-redo', ParamateUndo.handleTruncateRedoRequest);
    window.removeEventListener('as-custom-data-changed', ParamateUndo.onCustomDataChanged);
    window.removeEventListener('as-project-loaded', this._handleProjectLoaded);

    // Remove spinner if it exists
    if (this._spinner) {
      this._spinner.remove();
      this._spinner = null;
    }

    super.disconnectedCallback();
  }


  /**
   * A server session is live on `configurator`: after the first initialize()
   * in init(), and after every rotation in _reinitSession(). Records the
   * session, publishes the param table, syncs the registered inputs, restarts
   * the keepalive and clears the session flags in sysData.
   */
  private _sessionStarted(configurator: any): void {
    // Assigned before the publish below, so a subscriber woken by the first
    // publish can read `configurator` on this element. _bindConfigurator()
    // still runs the rest of the bind afterwards. On a reinit this is the
    // same instance, so the write changes nothing there.
    this.configurator = configurator;
    this._publishStagedWrites(configurator);

    if (configurator?.configSession) {
      currentProjectData.sessionId = configurator.configSession;
    }

    // The param store's other publish points are all configure/restore/load, so
    // without this the table stays empty until the first configure. Anything
    // binding right after init() (params.get(), params.subscribe,
    // bindElToParams) would read undefined and never be told otherwise.
    if (configurator?.params) {
      publishParams(configurator.params);
      ParamateUndo.suppressNext();
      this._syncParamValuesToInputs(configurator);
    }

    this.resetSessionTimout();

    // A lost session is over once a new one is up. sysData reads the session
    // id from internalData, written above, so it is not repeated here.
    _setSysData({
      configuratorReady: true,
      sessionLost: false,
      sessionRestoring: false,
      configuring: false,
    });
  }

  private _publishScheduled = false;

  /**
   * The store hands out the same table the SDK writes into, so a staged write
   * (`setParamValue(name, value, false)`) is visible to reads at once but told
   * to no subscriber. Wrap the SDK's own setParamValue, which every writer
   * goes through: this element, the inputs, the undo manager and app code
   * holding the instance. A write publishes once per microtask, however many
   * writes one handler makes. `dirty` on a published param says it is staged
   * and not yet configured. The reinit gate wraps the same method by name and
   * restores what it found, so the two compose.
   */
  private _publishStagedWrites(configurator: any): void {
    const original = configurator?.setParamValue;
    if (typeof original !== 'function' || original[STAGED_WRITE_PUBLISHES]) return;
    const wrapped = (...args: any[]) => {
      const result = original.apply(configurator, args);
      this._publishSoon(configurator);
      return result;
    };
    (wrapped as any)[STAGED_WRITE_PUBLISHES] = true;
    configurator.setParamValue = wrapped;
  }

  private _publishSoon(configurator: any): void {
    if (this._publishScheduled) return;
    this._publishScheduled = true;
    queueMicrotask(() => {
      this._publishScheduled = false;
      if (this.configurator === configurator && configurator.params) publishParams(configurator.params);
    });
  }

  get sessionId(){return this.configurator?.configSession}
  /** Live off the bound instance — was a plain field that was never assigned
   *  anywhere, so it always read back `undefined`. Reads correctly now, and
   *  (like sessionId) can't go stale across a reinit swap the way a cached
   *  copy could. */
  get configuratorId(): string | undefined { return this.configurator?.configuratorId; }
  /** See configuratorId above. */
  get apiKey(): string | undefined { return this.configurator?.APIKey; }

  /**
   * Wire the component to its configurator instance and run the post-init side
   * effects: hand the instance to everyone parked on waitForConfigurator(),
   * drain the queued operations, baseline the undo manager and announce
   * readiness with 'as-intrn-configurator-ready'. Runs once, from init(), after
   * _sessionStarted() has published the params and started the keepalive.
   */
  private _bindConfigurator(configurator: any): void {
    this.configurator = configurator;
    this.initialized = true;
    // A bind starts a new session generation too — anything still in the air
    // from before is stale (see _isStaleParamateEvent).
    this._cfgrGeneration++;

    devlog("AS-CONFIGURATOR: Configurator initialized", this.configurator);

    // Resolve any waitForConfigurator() promises taken before initialization.
    // Bound inputs park here too, through withConfigurator().
    for (const resolve of this._configuratorReadyResolvers) resolve(this.configurator);
    this._configuratorReadyResolvers = [];

    // Process any operations that were queued while waiting for initialization
    if (this._operationQueue.length > 0) {
      this.processOperationQueue();
    }

    (window as any).cfgr = this.configurator
    ParamateUndo.captureBaseline();
    ParamateUndo.markReady();
    ParamateUndo.clearSuppress();
    this.sendSystemInfoToQueue();
    this.clearHistory();
    // The library's "the CAD session is live" signal. as-app's init queue,
    // as-viewport and the step selector all hang off it.
    sendEv(window, 'as-intrn-configurator-ready', {
      detail: { sender: this, configurator: this.configurator }
    });
  }

  // ============================================================================
  // PROJECT EVENT HANDLERS
  // ============================================================================

  handleProjectNew = () => {
    devlog('[AS-CONFIGURATOR] as-after-project-new received');
    // A new project means a fresh session — the in-flight guard collapses this
    // with the as-reset-configurator that as-app's new-project flow also
    // dispatches, so we initialize() exactly once. Fire-and-forget:
    // _reinitSession() can reject, and nothing here awaits it, so catch
    // defensively rather than let it escape unhandled.
    this._reinitSession().catch((err: any) => devlog('[AS-CONFIGURATOR] project-new reinit failed:', err));
    // The old session (and any files uploaded into it) is simply abandoned when
    // reinitializing — skip the per-token deleteFile pass instead of racing it
    // against the session swap (see _reinitSession).
    this.clearHistory(true);
  }

  /** Give the component a brand-new server session on the SAME configurator
   *  instance — the in-place reinit is now the only path. The SDK's
   *  initialize() rotates token/configSession/params in three separate async
   *  stages on the live object (see
   *  docs/as-configurator.md#session-reinitialization), so this method walls
   *  those poisoned windows off from every caller instead of swapping in a
   *  fresh instance:
   *   - a call gate wraps the mutating entry points for the duration, queueing
   *     ANY caller — SDK Controls, apps holding a cached reference — behind
   *     the reinit. Same object, so the gate reaches callers the advisory
   *     as-app-busy-request flag never could;
   *   - a bounded idle wait lets requests already in flight settle before the
   *     rotation starts, so their responses can't land on a half-rotated
   *     instance;
   *   - a field snapshot is restored on failure, so a mid-rotation error can't
   *     leave the instance a new-token/old-session chimera — the old pair is
   *     still valid (getToken() doesn't revoke) and its session still live;
   *   - the generation counter (_cfgrGeneration) marks configure events from
   *     before the rotation as stale (see _isStaleParamateEvent).
   *  The abandoned session is never deleted client-side — its coordinates are
   *  gone from the instance once initialize() lands — so it TTL-expires
   *  server-side, files included.
   *
   *  Once initialize() resolves the new session is live; _sessionStarted()
   *  then restarts the heartbeat and syncs the new session's params, the same
   *  as after the first init. No SDK event is waited on.
   *
   *  Guarded so overlapping triggers (as-reset-configurator + as-after-project-new
   *  in the same new-project flow) collapse into a single reinit, and every
   *  caller gets the SAME completion promise — resolving early would let a
   *  caller (e.g. the expired-session restore) act against a session that
   *  isn't live yet. */
  private _reinitSession(): Promise<void> {
    if (!this.configurator) return Promise.resolve();
    if (this._reinitPromise) return this._reinitPromise;

    const cfgr = this.configurator;

    ParamateUndo.suppressNext();
    // Stop the keepalive for the session we're leaving; _sessionStarted()
    // restarts it against the new one.
    this.pauseSessionHeartbeat();

    // Release UI state an in-flight configure may hold: its after-configure is
    // dropped as stale (generation mismatch, see _isStaleParamateEvent), so it
    // would never clear its own busy flag or cancel-notification timer.
    this._clearConfigureTimer();
    this._closeCancelNotification();
    this._abandonConfigureWait?.();
    sendEv(window, 'as-app-busy-request', { detail: { key: 'configure', busy: false } });
    // ...and hold the app busy for the reinit itself — this is what keeps the
    // busy-gated header actions (New/Restore/Save, undo/redo) locked while the
    // session swap is in flight. Released in both completion paths below.
    sendEv(window, 'as-app-busy-request', { detail: { key: 'cfgr-reinit', busy: true } });

    // Everything opened before this line belongs to the outgoing session.
    this._cfgrGeneration++;

    // Call gate: queue the session-scoped entry points behind the reinit.
    // Installed on the instance itself, so it reaches every caller — SDK
    // Controls calling setParamValue on drag-end, apps calling a cached
    // reference — not just code that respects the advisory busy flag. getParams
    // and getObjectInfo stay unwrapped: initialize() calls them internally and
    // would deadlock behind its own gate (their one notable external caller,
    // the keepalive, is paused). getLog IS gated — a mid-rotation 500 routes
    // through classifyParamateError, whose log fetch would otherwise go out as
    // new-token + old-session (caught by the reinit stress suite). A queued
    // call runs once the reinit settles — into the NEW session on success, the
    // restored old one on failure — with the caller's own arguments, which is
    // the correct post-reinit behavior.
    const gatedMethods = ['configure', 'setParamValue', 'saveSession', 'restoreSession', 'uploadFile', 'deleteFile', 'getLog'];
    const gateStash: Array<{ name: string; hadOwn: boolean; original: any }> = [];
    for (const name of gatedMethods) {
      const hadOwn = Object.prototype.hasOwnProperty.call(cfgr, name);
      const original = cfgr[name];
      gateStash.push({ name, hadOwn, original });
      cfgr[name] = (...args: any[]) => {
        devlog(`[AS-CONFIGURATOR] ${name}() called during session reinit — queued until the new session is live`);
        return (this._reinitPromise ?? Promise.resolve())
          .catch(() => { /* a failed reinit restores a usable session — run the call either way */ })
          .then(() => original.apply(cfgr, args));
      };
    }
    // Uninstall without leaving own-property copies of prototype methods behind.
    const removeGate = () => {
      for (const { name, hadOwn, original } of gateStash) {
        if (hadOwn) cfgr[name] = original;
        else delete cfgr[name];
      }
    };

    // Field snapshot for the failure path — taken after the idle wait below,
    // so it reflects whatever state the last settled request left behind.
    let snapshot: Record<string, any> | null = null;

    this._reinitPromise = (async () => {
      // True once initialize() has resolved: from here a failure is in our own
      // post-rotation work and must not roll the live new session back.
      let rotated = false;
      try {
        // Bounded idle wait: let requests already in flight settle so their
        // responses land on a self-consistent instance rather than mid-rotation.
        // Best effort — past the deadline the rotation proceeds regardless, and
        // the generation guard drops whatever still lands late.
        const idleDeadline = Date.now() + REINIT_IDLE_WAIT_MS;
        while ((cfgr.fStatusConfigure
            || (cfgr.lastRequest && cfgr.lastRequest.readyState !== XMLHttpRequest.DONE))
          && Date.now() < idleDeadline) {
          await new Promise(r => setTimeout(r, 100));
        }
        snapshot = {
          token: cfgr.token,
          configSession: cfgr.configSession,
          renderingKey: cfgr.renderingKey,
          modelKey: cfgr.modelKey,
          params: cfgr.params,
          objectInfo: cfgr.objectInfo,
        };
        await cfgr.initialize();
        rotated = true;

        this._sessionStarted(cfgr);
        // _sessionStarted synced the new session's params, so this snapshots
        // the NEW baseline.
        ParamateUndo.captureBaseline();
        // initialize() never resets the SDK's own undo stack — left alone, undo()/redo()
        // on the new session could configure() old-session param snapshots into it.
        cfgr.undoSteps = [];
        cfgr.undoStep = 0;
        devlog('[AS-CONFIGURATOR] Re-initialization complete (in place, same instance)');
      } catch (err: any) {
        if (rotated) {
          devlog('[AS-CONFIGURATOR] Post-completion error during reinit — ignoring:', err);
          return;
        }
        // initialize() can reject (getToken/startConfigSession network or server
        // failure). Roll the rotated fields back to the pre-reinit pair: it is
        // still valid (getToken() doesn't revoke the old token) and its session
        // is still live, so the instance stays usable instead of a chimera. A
        // half-created NEW session (startConfigSession succeeded, a later stage
        // failed) is simply abandoned to TTL-expire.
        if (snapshot) Object.assign(cfgr, snapshot);
        this.resetSessionTimout();
        devlog('[AS-CONFIGURATOR] Re-initialization failed (rolled back to previous session):', err);
        throw err;
      } finally {
        this._reinitPromise = null;
        removeGate();
        ParamateUndo.clearSuppress();
        sendEv(window, 'as-app-busy-request', { detail: { key: 'cfgr-reinit', busy: false } });
      }
    })();
    return this._reinitPromise;
  }

  /** Force a brand-new server session and resolve once it is live. Public API for
   *  apps that need to recover from a dead session (e.g. the machine slept past the
   *  session TTL): calls against the dead session id 404 ("route not found"), so a
   *  fresh session must be established first. The built-in expiry flow no longer
   *  calls this — its Start New Session button reinits via the new-project path. */
  refreshSession(): Promise<void> {
    return this._reinitSession();
  }

  handleResetConfigurator = () => {
    if (this.configurator) {
      // A genuinely fresh server session, no residual state. Guarded inside
      // _reinitSession so it collapses with the as-after-project-new reinit fired in
      // the same flow.
      devlog('[AS-CONFIGURATOR] Reinitializing configurator for new project');
      this._reinitSession().catch((err: any) => devlog('[AS-CONFIGURATOR] reset-configurator reinit failed:', err));
    }
    // The reinit abandons the whole old session — skip the per-token
    // deleteFile pass (the abandoned session's files go with it; no point
    // chasing individual tokens on the way out).
    this.clearHistory(true);
  }

  handleConfigurePending = () => {
    this.configurePending();
  }

  /** Arrow field so the listener identity stays stable across add/remove. */
  handleInputChange = (ev: Event) => handleParamInputChange(ev);

  /** Strip the `main:` prefix so upload-token tracking keys are consistent regardless of how
   *  the caller specified the param name. configurator.params uses `main:foo`; trcad-param
   *  attributes may or may not include the prefix. */
  private static _normKey(k: string): string { return normalizeTokenKey(k); }


  // ============================================================================
  // PARAMATE EVENT HANDLERS
  // ============================================================================

  /** True when a paramate window event should be ignored as reinit fallout.
   *  A session reinit rotates the session on the SAME instance without
   *  aborting its in-flight request (an abort would reject the awaiting caller
   *  with a synthetic 499); the SDK dispatches its before/after-configure
   *  window events unconditionally, so stragglers still surface here. Reacting
   *  to one would sync the dead session's params into the inputs — or, for a
   *  before-configure, set a busy flag whose matching after-configure never
   *  clears it. Two checks:
   *   - while a reinit is in flight, NO configure traffic is legitimate (the
   *     rotation itself never configures, and everything else is gated or
   *     belongs to the outgoing session);
   *   - with checkGeneration (after-configure), a cycle whose before-configure
   *     opened under an older generation is a straggler that outlived the
   *     reinit — the instance is the same, so only the counter can tell. */
  private _isStaleParamateEvent(event: CustomEvent, opts: { checkGeneration?: boolean } = {}): boolean {
    if (this._reinitPromise) {
      devlog('[AS-CONFIGURATOR] Ignoring', event.type, 'during session reinit');
      return true;
    }
    if (opts.checkGeneration && this._configureGen !== this._cfgrGeneration) {
      devlog('[AS-CONFIGURATOR] Ignoring', event.type, 'from a configure cycle that predates the session reinit');
      return true;
    }
    return false;
  }

  /**
   * Handle paramate-configurator-before-configure event
   * Note: This fires AFTER onBeforeConfigure handler (which can block)
   */
  handleParamateBeforeConfigure = (event: CustomEvent) => {
    if (this._isStaleParamateEvent(event)) return;
    // Stamp the generation this configure cycle opened under — its
    // after-configure is only honored if the session hasn't rotated since.
    this._configureGen = this._cfgrGeneration;
    const { changed, cancelled } = event.detail || {};
    devlog('[AS-CONFIGURATOR] paramate-configurator-before-configure:', {
      changed,
      cancelled
    });

    if (!cancelled) {
      // A frame still owed by the last configure must not release this one.
      this._abandonConfigureWait?.();
      sendEv(window, 'as-app-busy-request', { detail: { key: 'configure', busy: true } });
    }

    ParamateUndo.beforeConfigure();

    this._startConfigureTimer();
  }

  /**
   * Handle paramate-configurator-after-restore event
   * We sync ground truth values back to inputs after a session restore
   */
  handleParamateAfterRestore = (event: CustomEvent) => {
    if (this._isStaleParamateEvent(event)) return;
    const configurator = this.configurator;
    if (!configurator?.params) return;

    devlog('[AS-CONFIGURATOR] paramate-configurator-after-restore: Syncing restored values');

    // Flag to suppress history during sync
    ParamateUndo.suppressNext();

    // Sync CAD values → Store
    this._syncParamValuesToInputs(configurator);

    // Update snapshots for future undo/redo
    const baseline = ParamateUndo.captureBaseline();

    // Publishes to param subscribers/bindings, and dispatches the
    // 'as-paramate-params-updated' window event (see store/paramStore.ts).
    publishParams(configurator.params);

    // Explicitly notify inputs to update their visual state
    sendEv(window, 'as-restore-param-values', {
      detail: { params: baseline }
    });

    // For project loads, suppress stays true until _handleProjectLoaded clears
    // it. (New-project resets go through _reinitSession, which clears its own
    // suppress on completion — no restore is involved there any more.)
  }

  /**
   * Handle paramate-configurator-after-configure event
   * This is where we sync ground truth values back to inputs
   * Note: This fires AFTER onAfterConfigure handler
   */
  handleParamateAfterConfigure = (event: CustomEvent) => {
    if (this._isStaleParamateEvent(event, { checkGeneration: true })) return;
    this._clearConfigureTimer();
    this._closeCancelNotification();

    const configurator = this.configurator;

    if (!configurator?.params) {
      console.warn('[AS-CONFIGURATOR] No params available after configure');
      return;
    }

    // The SDK fires after-configure while the new model is still downloading,
    // so the busy holds until the viewport has drawn it.
    if (configurator.viewPort) {
      this._abandonConfigureWait = this._releaseBusyAfterFrame('configure', this._nextModelFrame(configurator.viewPort));
    } else {
      sendEv(window, 'as-app-busy-request', { detail: { key: 'configure', busy: false } });
    }

    this._syncParamValuesToInputs(configurator);

    // Publish BEFORE completing history so that any appData reactions can be
    // squashed if needed.
    publishParams(configurator.params);

    ParamateUndo.afterConfigure();
  }

  private _syncParamValuesToInputs(configurator: any): void {
    syncParamValuesToInputs(configurator);
  }

  /**
   * Handle paramate-configurator-error event
   * Note: This fires for any API error, not just configuration errors
   */
  handleParamateError = async (event: CustomEvent) => {
    this._clearConfigureTimer();
    this._closeCancelNotification();
    this._abandonConfigureWait?.();
    sendEv(window, 'as-app-busy-request', { detail: { key: 'configure', busy: false } });

    // Clear skipHistory flag — a failed configure must not suppress history for the next call.
    ParamateUndo.clearSuppress();

    const { err } = event.detail || {};

    ParamateUndo.onConfigureError();

    // Clear pending state on error - inputs should revert to last known good value
    const registeredParams = getRegisteredParamNames();
    for (const paramName of registeredParams) {
      // On error, resolve with undefined to clear pending without changing value
      // The input should handle this by keeping its current value
      resolveParamPending(paramName, undefined);
    }

    const classification = await classifyParamateError(err ?? {}, {
      getLog: this.sessionId ? () => this.configurator.getLog() : undefined,
      hasActiveSession: !!this.sessionId,
      saveInFlight: this._saveInFlight,
    });

    // A 401/404 is how an expired session shows itself, and a 499 is a request
    // a newer one replaced. Neither is an error.
    if (classification.case === 'session-expired') devlog('[AS-CONFIGURATOR] paramate-configurator-error (session-auth):', err);
    else if (classification.case === 'ignore') devlog('[AS-CONFIGURATOR] paramate-configurator-error (aborted):', err);
    else console.error('[AS-CONFIGURATOR] paramate-configurator-error:', err);

    // Dispatch error event for UI handling / app-specific side effects — this is
    // the hook apps use instead of a custom debug.error, now enriched with the
    // classification (see paramateErrorInterceptor.ts).
    sendEv(window, 'as-paramate-error', {
      detail: { error: err, classification }
    });

    if (classification.case === 'ignore') return;
    // restoreSession()'s own onFail owns the UI for a restore-triggered failure —
    // including a 404/401 on a dead session, which classifyParamateError would
    // otherwise route into the session-expired branch below and pop the
    // expired-session modal on top of (or instead of) restoreSession's own
    // failure UI, racing two independent recovery flows off the same error.
    if (this._restoreInFlight) return;
    if (classification.case === 'session-expired') {
      // While a reinit is in flight, a 401/404 is a straggler aimed at the
      // abandoned session, not expiry. Routing it into the expired-session flow
      // would offer to restore the OLD project blob into the brand-new session —
      // the "configure with old state" cascade (docs/as-configurator.md#session-reinitialization).
      if (this._reinitPromise) {
        devlog('[AS-CONFIGURATOR] session-auth error during reinit — ignoring:', err);
        return;
      }
      // A keepalive is outstanding: this is (most likely) its own 404 coming
      // in through the event side of the SDK's double report, and its catch
      // knows exactly which session it went to. Leave the verdict to it — if
      // this was some other request's 404, the beat's result settles the
      // question anyway.
      if (this._heartbeatInFlight) {
        devlog('[AS-CONFIGURATOR] session-auth error while the keepalive is in flight — deferring to it:', err);
        return;
      }
      // A reinit that already FINISHED can still leave stragglers in the air,
      // 401ing against the session it rotated away — indistinguishable here
      // from the bound session actually expiring. Confirm before starting the
      // expiry flow. The probe's own failure comes back through this handler,
      // which _sessionLostProbe absorbs.
      if (this._sessionLostProbe) return;
      this._confirmSessionLost(err);
      return;
    }

    this._app?.openModal('paramate-error', 'as-modal-paramate-error');
    (this._app?.modalOverlay?.querySelector('#paramate-error') as any)?.setContent({
      ...classification,
      sessionId: currentProjectData.sessionId,
    });
    if (classification.sendLog) {
      (this._app as any)?.sendLog?.('paramate_error', classification.case, {
        status: err?.status,
        message: err?.response?.message ?? err?.response?.info,
      });
    }
  }

  // ============================================================================
  // CONFIGURE TIMEOUT / CANCEL
  // ============================================================================

  /** Both names close the same thing — the slow-configure notice. Kept as two
   *  methods because the callers read better that way (one clears a timer that
   *  may never have fired, the other dismisses a notification that may never
   *  have appeared) and startSlowOpNotice handles both cases in one call. */
  private _clearConfigureTimer() {
    this._configureNotice?.close();
    this._configureNotice = null;
  }

  private _closeCancelNotification() {
    this._clearConfigureTimer();
  }

  private _startConfigureTimer() {
    this._clearConfigureTimer();

    this._configureNotice = startSlowOpNotice({
      delayMs: cancelMessageDelayMs(this, 15),
      defaultText: 'This process is taking a while. Would you like to cancel and adjust?',
      dictKey: 'notifications.configure.timeout',
      app: this._app,
      onCancel: () => this._handleCancelConfigure(),
    });
  }

  private _handleCancelConfigure() {
    this.configurator?.cancelLastRequest?.();
    this._closeCancelNotification();

    // Clear pending states on all registered inputs
    const registeredParams = getRegisteredParamNames();
    for (const paramName of registeredParams) {
      resolveParamPending(paramName, undefined);
    }
  }

  // ============================================================================
  // SESSION FILE LOOKUP
  // ============================================================================

  /**
   * Handle as-get-session-file requests from as-file-input components.
   * Fetches the current session zip, locates the file binaries for the given
   * param, and resolves with { blob, filename, files } — blob/filename are
   * the first stored file (the pre-multi-file shape, kept for compatibility),
   * files lists every stored file as { blob, filename } (several for `file[]`
   * array params). Resolves null when nothing is stored.
   */
  private _handleGetSessionFile = async (ev: Event): Promise<void> => {
    const { paramName, resolve, reject } = (ev as CustomEvent).detail ?? {};
    if (!paramName || !resolve) return;
    if (!this.initialized) { resolve(null); return; }

    try {
      const sessionBlob = await this.getSessionBlob();
      const zip = unzipSync(new Uint8Array(await sessionBlob.arrayBuffer()));

      // Locate the param and read its token value(s) — a scalar file param
      // holds one token string, a file[] param an array of tokens.
      const paramsJson = JSON.parse(strFromU8(zip['parameters.json']));
      const packages: any[] = paramsJson?.objects?.[0]?.packages ?? [];
      let tokens: string[] = [];
      outer: for (const pkg of packages) {
        for (const param of [...(pkg.oparams ?? []), ...(pkg.pparams ?? [])]) {
          if (`${pkg.ident}:${param.ident}` === paramName) {
            const raw = param.value;
            tokens = (Array.isArray(raw) ? raw : [raw])
              .map((t: any) => (t == null ? '' : String(t)))
              .filter(Boolean);
            break outer;
          }
        }
      }
      if (!tokens.length) { resolve(null); return; }

      // Filename lookup table from files.json (fall back to a placeholder).
      let filesArr: any[] = [];
      try {
        const parsed = JSON.parse(strFromU8(zip['files.json']));
        if (Array.isArray(parsed)) filesArr = parsed;
      } catch { /* leave empty — placeholder names below */ }

      // Binaries are stored at files/<token> in the session zip.
      const files: Array<{ blob: Blob; filename: string }> = [];
      for (const token of tokens) {
        const fileData = zip[`files/${token}`];
        if (!fileData?.length) continue;
        const entry = filesArr.find(f => f.id === token || f.token === token);
        files.push({ blob: new Blob([fileData]), filename: entry?.name ?? 'Restored from session' });
      }
      if (!files.length) { resolve(null); return; }

      resolve({ blob: files[0].blob, filename: files[0].filename, files });
    } catch (err) {
      devlog('[as-configurator] as-get-session-file failed:', err);
      reject?.(err);
    }
  };

  // ============================================================================
  // PUBLIC API
  // ============================================================================

  /**
   * Initialises Paramate in one call: creates the Configurator and the
   * ViewportWebGL, awaits the SDK's initialize() and binds the component to
   * the result. The only way the component gets its instance.
   *
   * Configurator credentials are resolved in priority order:
   *   1. opts.configuratorDetails  (explicit, takes precedence)
   *   2. data/app.cfg.json → configuratorDetails  (via getAppConfig)
   *
   * All standard Paramate Configurator options (onAfterConfigure, undoLevel, etc.)
   * can be passed in opts and are forwarded directly.
   */
  async init(opts: AsConfiguratorInitOptions = {}): Promise<void> {
    if (this.initialized) {
      console.warn('[as-configurator] init() called again on an initialised element. Use refreshSession() for a new server session.');
      return;
    }
    sendEv(window, 'as-app-busy-request', { detail: { key: 'cfgr-init', busy: true } });
    const releaseBusy = () => sendEv(window, 'as-app-busy-request', { detail: { key: 'cfgr-init', busy: false } });
    let firstFrame: { done: Promise<void>; cancel: () => void } | undefined;
    try {
      const Paramate = (window as any).Paramate;
      if (!Paramate) throw new Error('[as-configurator] Paramate not found on window');
      // The eager patch at paramateErrorInterceptor import time no-ops when the
      // SDK script is loaded AFTER as-components (Outlines loads it on demand).
      // Idempotent, so re-apply here — without it the constructor below is the
      // raw SDK one and its default debug.error alert()s on every failed
      // request (e.g. the two expected 404s of the expiry heartbeat + probe).
      installParamateConstructorPatch();

      // Resolve credentials — priority order:
      //   1. opts.configuratorDetails (explicit)
      //   2. data/app.cfg.json → configuratorDetails (via getAppConfig)
      //
      // Which tier (local/dev/staging/production) of those details is used
      // lives in components/paramate/lib/configuratorTier.ts — see its doc for the full rules
      // (?server= URL param, local-server attribute, local-server probe).
      const appId = internalData.appId ?? asApp()?.getAttribute('app-id') ?? '';
      let cd = opts.configuratorDetails;
      if (!cd) {
        const appCfg = await getAppConfig(appId);
        if (!appCfg.configuratorDetails) throw new Error(`[as-configurator] No configurator details found for "${appId}". Add a configuratorDetails key to data/app.cfg.json or pass them to init().`);
        cd = appCfg.configuratorDetails;
      }
      const resolved = await resolveConfiguratorDetails(cd, { preferLocalServer: this.hasAttribute('local-server') });
      const details: ParamateDetails | undefined = resolved?.details;
      // Which tier won isn't carried by any event, so publish it here.
      if (resolved?.tier) _setSysData({ tier: resolved.tier });

      if (!details?.configuratorId || details?.APIKey == null) {
        throw new Error(isOnPrem
          ? '[as-configurator] deployment="onprem" needs configuratorDetails.local with APIHub, configuratorId and APIKey in data/app.cfg.json'
          : '[as-configurator] configuratorId and APIKey are required');
      }

      // Find viewport element
      const vpEl = opts.viewportElement !== undefined
        ? opts.viewportElement
        : wcQry(AsViewport);

      // Strip init-specific keys; forward the rest to Paramate
      const { configuratorDetails, viewportElement, viewportOptions, ...paramateOpts } = opts;

      const settings = await getAppConfig(internalData.appId ?? '');
      let apiHub: string;
      if (isOnPrem) {
        // The local entry holds the whole on-prem endpoint.
        const ep = settings.paramate?.onPrem;
        if (details.APIHub) {
          apiHub = details.APIHub;
        } else if (ep) {
          console.warn('[DEPRECATED] paramate.onPrem in app.cfg.json: put the on-prem Paramate address in configuratorDetails.local.APIHub.');
          apiHub = `${window.location.protocol}//${ep.host}${ep.port ? `:${ep.port}` : ''}`;
        } else {
          throw new Error('[as-configurator] env="onprem" needs configuratorDetails.local.APIHub in data/app.cfg.json');
        }
      } else if (settings.paramate?.external) {
        const ep = settings.paramate.external;
        const portStr = ep.port ? `:${ep.port}` : '';
        apiHub = `https://${ep.host}${portStr}`;
      } else {
        apiHub = details.APIHub ?? DEFAULT_PARAMATE_API_HUB;
      }

      // The Paramate.Configurator constructor is patched globally (see
      // installParamateConstructorPatch() above) to suppress the SDK's
      // built-in alert() on errors — handleParamateError shows classified UI
      // via the paramate-configurator-error window event instead. No debug
      // override needed here; forward paramateOpts (including any debug/
      // onConfigureError the caller supplied) unmodified.
      const configurator = new Paramate.Configurator({
        APIHub: apiHub,
        configuratorId: details.configuratorId,
        APIKey: details.APIKey,
        ...paramateOpts,
      });

      if (vpEl) {
        // Paramate draws a wait animation of its own inside the viewport on
        // every configure, and the app already shows one for the same state, so
        // switch Paramate's off rather than show two. viewportOptions is spread
        // after, so naming useWaitAnim there still decides.
        //
        // as-app creates the spinner on DOMContentLoaded, which a deferred
        // module calling init() beats - so ask for it here rather than assume.
        const appHasSpinner = asApp()?.ensureBusySpinner()
          ?? !!document.querySelector('#as-main-content > as-spinner');
        const viewPort = new Paramate.ViewportWebGL({
          useWaitAnim: !appHasSpinner,
          ...viewportOptions,
          configurator,
          viewportElement: vpEl,
        });
        configurator.viewPort = viewPort;
        // The model loads and first draws inside initialize(), so listen before it.
        firstFrame = this._nextModelFrame(viewPort);
      }

      await configurator.initialize();
      this._sessionStarted(configurator);
      this._bindConfigurator(configurator);
    } catch (err) {
      firstFrame?.cancel();
      releaseBusy();
      throw err;
    }
    // initialize() resolves once the session and model are up, before the
    // viewport has drawn a frame. The first draw starts at the end of
    // initialize(), or once the last material texture has loaded, so the busy
    // holds until that frame. init() itself resolves now; only the busy waits.
    if (firstFrame) this._releaseBusyAfterFrame('cfgr-init', firstFrame);
    else releaseBusy();
  }

  /** Drops the wait a configure left behind without releasing its busy. */
  private _abandonConfigureWait: (() => void) | null = null;

  /** Resolves once this viewport has drawn its next model: the first
   *  'paramate-viewport-after-redraw' after its 'paramate-viewport-model-update'.
   *  A camera redraw while the model is still downloading does not count. The
   *  non-WebGL viewport has no reloadModel() and no model-update event, so its
   *  next redraw counts. cancel() drops the listeners and resolves early. */
  private _nextModelFrame(viewPort: any): { done: Promise<void>; cancel: () => void } {
    let cancel!: () => void;
    const done = new Promise<void>((resolve) => {
      let modelLoaded = typeof viewPort?.reloadModel !== 'function';
      const isOurs = (ev: Event) => (ev as CustomEvent).detail?.viewport === viewPort;
      const onModel = (ev: Event) => { if (isOurs(ev)) modelLoaded = true; };
      const onRedraw = (ev: Event) => { if (modelLoaded && isOurs(ev)) cancel(); };
      cancel = () => {
        window.removeEventListener('paramate-viewport-model-update', onModel);
        window.removeEventListener('paramate-viewport-after-redraw', onRedraw);
        resolve();
      };
      window.addEventListener('paramate-viewport-model-update', onModel);
      window.addEventListener('paramate-viewport-after-redraw', onRedraw);
    });
    return { done, cancel };
  }

  /** Releases the busy `key` once `frame` resolves, or after REDRAW_FALLBACK_MS
   *  when no frame comes, so the busy cannot stick. Returns an abandon function
   *  that drops the wait without releasing. */
  private _releaseBusyAfterFrame(key: string, frame: { done: Promise<void>; cancel: () => void }): () => void {
    let live = true;
    const fallback = setTimeout(() => {
      devlog('[AS-CONFIGURATOR] No viewport frame within', REDRAW_FALLBACK_MS, 'ms, releasing the', key, 'busy');
      frame.cancel();
    }, REDRAW_FALLBACK_MS);
    frame.done.then(() => {
      clearTimeout(fallback);
      if (live) sendEv(window, 'as-app-busy-request', { detail: { key, busy: false } });
    });
    return () => {
      live = false;
      clearTimeout(fallback);
      frame.cancel();
    };
  }

  getMeshUrl(type = 'stl', filename = 'model.stl', finalized = true): string | null {
    if (!this.configurator) return null;
    return finalized 
      ? this.configurator.getFinalizedModelUrl(type, filename)
      : this.configurator.getModelUrl();
  }

  async getMesh(type = 'stl', filename = 'model.stl', finalized = true): Promise<Blob> {
    if (!this.configurator) throw new Error('[as-configurator] not initialized');
    const url = this.getMeshUrl(type, filename, finalized);
    if (!url) throw new Error(`[as-configurator] Could not get mesh URL from configurator`);
    const res = await fetch(url);
    if (!res.ok) {
      // This is a raw fetch, not a configurator.doRequest() call, so it never
      // goes through the SDK's debug.error/paramate-configurator-error pipeline —
      // classify it here ourselves so a mesh-server timeout surfaces as
      // "Operation Timeout" instead of a bare "500 Internal Server Error".
      let response: { message?: string; info?: string; [k: string]: any } | undefined;
      try { response = await res.clone().json(); } catch { /* body wasn't JSON */ }
      const classification = await classifyParamateError(
        { status: res.status, statusText: res.statusText, response },
        { hasActiveSession: !!this.configurator?.configSession, saveInFlight: false },
      );
      // Mirror handleParamateError's session-expired handling: a mesh 404/401 means
      // the same dead session, just discovered via a raw fetch() instead of the SDK's
      // own request pipeline, so it never reaches handleParamateError on its own.
      // Same reinit/restore-in-flight guards and the same liveness probe — a
      // straggler against a rotated-away session, or a restore-triggered failure
      // already owned by restoreSession's own onFail, must not pop the
      // expired-session modal.
      if (classification.case === 'session-expired' && !this._reinitPromise && !this._restoreInFlight) {
        this._confirmSessionLost({ status: res.status, statusText: res.statusText, response });
      }
      const message = 'defaultMessage' in classification ? classification.defaultMessage : `Failed to fetch mesh: ${res.status} ${res.statusText}`;
      throw Object.assign(new Error(message), { status: res.status, statusText: res.statusText, response, classification });
    }
    return res.blob();
  }

  getFile(fileId: string): Promise<Blob | null> {
    if (!this.configurator) return Promise.reject(new Error('Configurator not initialised'));
    return new Promise((resolve) => {
      const p = this.configurator.getFile(fileId, (result: unknown) => {
        resolve(result instanceof Blob ? result : null);
      });
      if (p && typeof p.catch === 'function') p.catch(() => resolve(null));
    });
  }

  /** The param table, once the configurator has initialised. The same object
   *  `params.all()` returns; this just waits for it to exist. */
  async getParams(): Promise<Record<string, ParamData>> {
    await this.waitForConfigurator();
    return params.all();
  }

  /** Resolves with the live Paramate configurator once it is initialised — resolves
   *  immediately if already ready, otherwise at the moment init() binds it
   *  (`_bindConfigurator`). Handy for add-on components such as <as-gui-ctrls>. */
  waitForConfigurator(): Promise<any> {
    if (this.initialized && this.configurator) return Promise.resolve(this.configurator);
    return new Promise((resolve) => { this._configuratorReadyResolvers.push(resolve); });
  }

  private _getSession = async (): Promise<Blob> => {
    this._saveInFlight = true;
    try {
      const origBlob: Blob = await this.configurator.saveSession();
      return new Blob([origBlob], { type: 'application/zip' });
    } finally {
      this._saveInFlight = false;
    }
  }

  /** Return the current Paramate session as a zip Blob without side effects. */
  getSessionBlob = (): Promise<Blob> => this._getSession();

  /**
   * Flat { "pkg:param": value } map of pending params — values set client-side
   * via `setParamValue(..., false)` that no configure has pushed to the server
   * yet. The SDK marks these `dirty` and both configure() and restoreSession()
   * replace the whole param table with the server's response, clearing the
   * flags — so dirty entries are exactly the un-flushed edits. Values are live
   * references, not clones.
   */
  getPendingParams(): Record<string, any> {
    return getPendingParams(this.configurator);
  }

  /**
   * Capture the current Paramate session zip and store it on the active project,
   * along with the current pending params (see getPendingParams). The blob is
   * kept exactly as the server produced it — pending edits are NOT baked in.
   * Instead loadProject replays them via applyPendingParams() after the restored
   * session settles, so a project saved in a pending state is restored in that
   * same pending state: baseline params and geometry from the blob, the
   * un-flushed edits dirty on top, exactly as at save time.
   */
  storeSession = async (): Promise<Blob> => {
    const blob = await this._getSession();
    if (internalData.currentProject) {
      internalData.currentProject.paramateSession = blob;
      // options.pendingParams === false opts the app out completely: nothing is
      // captured here, and loadProject skips the replay for the same reason.
      const pending = this._options.pendingParams === false ? {} : this.getPendingParams();
      // Always assign — clears stale pending from an earlier save.
      internalData.currentProject.pendingParams =
        Object.keys(pending).length > 0 ? pending : undefined;
    }
    return blob;
  }

  /**
   * Replay pending params captured at save time (Project.pendingParams) onto
   * the live session: each value is re-set with setParamValue(..., false) — so
   * it's dirty again, with no configure triggered — recreating the pre-submit
   * state the project was saved in. Keys missing from the live param table
   * (e.g. renamed by a migration since the save) are skipped. Returns the
   * number of params applied.
   */
  applyPendingParams(pending: Record<string, any>): number {
    const params = this.configurator?.params ?? {};
    let applied = 0;
    for (const [key, value] of Object.entries(pending)) {
      if (key in params) {
        this.setParamValue(key, value, false);
        applied++;
      }
    }
    if (applied > 0) devlog(`[AS-CONFIGURATOR] applyPendingParams: replayed ${applied} pending param(s) from the saved project`);
    return applied;
  }
  restoreSession(file: File | Blob){
    // Suppresses the generic classified-error modal for the doRequest() failure
    // this also triggers underneath — this method's own onFail is the specific,
    // known case (wrong/incompatible file) and owns the resulting UI.
    this._restoreInFlight = true;
    const onFail = (err: any) => {
      this._restoreInFlight = false;
      console.error('[AS-CONFIGURATOR] restoreSession failed:', err);
      sendEv(window, 'as-session-restore-failed', {
        detail: { error: err }
      });
      this._app?.openModal('paramate-error', 'as-modal-paramate-error');
      (this._app?.modalOverlay?.querySelector('#paramate-error') as any)?.setContent({
        titleKey: 'paramateError.sessionFileNotRecognized.title',
        defaultTitle: 'Session File Not Recognized',
        messageKey: 'paramateError.sessionFileNotRecognized.message',
        defaultMessage: 'The provided file could not be restored as a session. Please check your file, it may be incompatible.',
        sessionId: currentProjectData.sessionId,
      });
    };
    // The SDK's restoreSession PUTs to /configSessions/{id}/restore and returns a
    // promise that can reject (e.g. 404 on an expired session id) WITHOUT invoking
    // `fail`. Catch the returned promise too so the rejection can't escape as an
    // "Uncaught (in promise)"; the after-restore listener removal makes a double
    // as-session-restore-failed dispatch harmless.
    const p = this.configurator.restoreSession({ file, fail: onFail });
    Promise.resolve(p).then(() => { this._restoreInFlight = false; }).catch(onFail);
  }

  /**
   * The param table, the same object `<as-app>.params` and the `params` export
   * hand out. `params` means one thing wherever you reach for it.
   *
   * This element is where it comes from - every publishParams() below is what
   * fills it - so reading it back off the element is reading the store it
   * feeds, not a second copy. Before the first publish it is an empty table
   * rather than undefined, so `params.get(...)` is safe to call at any point.
   *
   * Read-only. Writes still go through this element's setParamValue().
   */
  get params(): typeof params {
    return params;
  }

  /**
   * Get the current value of a configurator parameter.
   * Returns undefined if the configurator is not yet initialized or the param does not exist.
   */
  getParamValue(paramName: string): any {
    return this._passThrough('getParamValue', paramName);
  }

  /**
   * Batch-read several param values at once — instead of a separate
   * getParamValue() call per param.
   *
   * Array form: `const [colors, margins] = asConfiguratorEl.getParamValues(['main:uploads:colors', 'main:uploads:margin']);`
   * — pairs with array destructuring, but the param tag and the local variable
   * name are two separate lists that only line up by position; reordering one
   * without the other silently mismatches everything after it.
   *
   * Object form: `const { colors, margins } = asConfiguratorEl.getParamValues({ colors: 'main:uploads:colors', margins: 'main:uploads:margin' });`
   * — the alias sits right next to its tag, so native object destructuring
   * can't drift out of sync the way the array form can. Prefer this form for
   * new call sites.
   *
   * Either way, a typo'd tag silently becomes an `undefined` variable with no
   * error at the call site, easy to miss buried in a list — so (unlike the
   * single getParamValue) each result is checked against the param map and
   * warned individually by tag if it's actually missing, not just legitimately
   * undefined-valued.
   */
  getParamValues(names: string[]): any[];
  getParamValues(aliasToName: Record<string, string>): Record<string, any>;
  getParamValues(namesOrMap: string[] | Record<string, string>): any[] | Record<string, any> {
    return readParamValues(this.configurator, this.initialized, namesOrMap);
  }

  /**
   * Parse a Paramate vector-param string (e.g. `<[1,2,3]>`) into a plain number array.
   * Pass-through to the live configurator — see getParamValue for why apps should call
   * this on as-configurator rather than caching the configurator instance themselves.
   */
  parseVector(value: string): number[] | undefined {
    return this._passThrough('parseVector', value);
  }

  /** Inverse of parseVector — format a plain number array back into a Paramate vector-param string. */
  vectorToParam(value: number[]): string | undefined {
    return this._passThrough('vectorToParam', value);
  }

  /** Parse a Paramate matrix44-param string into Paramate's row-major matrix array format. */
  deserializeMatrix44(value: string): number[][] | undefined {
    return this._passThrough('deserializeMatrix44', value);
  }

  /** Inverse of deserializeMatrix44 — format a matrix array back into a Paramate matrix44-param string. */
  serializeMatrix44(value: number[][]): string | undefined {
    return this._passThrough('serializeMatrix44', value);
  }

  /** Call a configurator method once it exists, warning by name if it does not.
   *  Backs the pass-through params API, where every method has this same shape. */
  private _passThrough(method: string, ...args: any[]): any {
    if (!this.initialized) {
      console.warn(`[as-configurator] ${method} called before initialization`);
      return undefined;
    }
    return this.configurator[method](...args);
  }

  /**
   * Set a parameter value and optionally trigger a configuration cycle.
   *
   * The positional form matches stock Paramate's
   * `setParamValue(name, value, triggerConfigure, callback)`, so a raw
   * `configurator.setParamValue(...)` call moves to this wrapper unchanged.
   * `skipHistory` rides in a trailing options object, after the callback.
   *
   * Overload 1 — positional:
   *   `setParamValue('main:width', 100)`
   *   `setParamValue('main:width', 100, true, () => console.log('configured'))`
   *   `setParamValue('main:width', 100, true, undefined, { skipHistory: true })`
   *
   * Overload 2 — options object:
   *   `setParamValue({ name: 'main:width', value: 100, triggerConfigure: false, skipHistory: true })`
   */
  setParamValue(name: string, value: any, triggerConfigure?: boolean, callback?: Function, options?: ParamateHistoryOptions): Promise<any> | undefined;
  setParamValue(options: { name: string; value: any; triggerConfigure?: boolean; skipHistory?: boolean; callback?: Function }): Promise<any> | undefined;
  setParamValue(
    nameOrOptions: string | { name: string; value: any; triggerConfigure?: boolean; skipHistory?: boolean; callback?: Function },
    value?: any,
    triggerConfigure = true,
    callbackOrLegacyFlag?: Function | boolean,
    options?: ParamateHistoryOptions
  ): Promise<any> | undefined {
    let paramName: string;
    let paramValue: any;
    let trigger: boolean;
    let isSkipHistory: boolean;
    let callback: Function | undefined;

    if (typeof nameOrOptions === 'object') {
      paramName     = nameOrOptions.name;
      paramValue    = nameOrOptions.value;
      trigger       = nameOrOptions.triggerConfigure ?? true;
      isSkipHistory = nameOrOptions.skipHistory ?? false;
      callback      = nameOrOptions.callback;
    } else {
      const arg     = readCallbackArg('setParamValue', callbackOrLegacyFlag, '{ skipHistory: true } as the fifth argument');
      paramName     = nameOrOptions;
      paramValue    = value;
      trigger       = triggerConfigure;
      callback      = arg.callback;
      isSkipHistory = options?.skipHistory ?? arg.skipHistory;
    }

    if (isSkipHistory) ParamateUndo.suppressNext();
    return this.configurator?.setParamValue(paramName, paramValue, trigger, callback);
  }

  /**
   * Trigger a CAD configuration cycle.
   *
   * The first two arguments match stock Paramate's `configure(changes, callback)`.
   * `skipHistory` rides in a trailing options object, after the callback.
   *
   * @param changes - Optional parameter overrides in Paramate format: `{ 'main:paramName': { value: ... }, ... }`.
   *                  Pass `undefined` or `{}` to configure with the current staged values.
   * @param callback - Optional callback fired by Paramate after the configure completes (same timing as onAfterConfigure).
   * @param options - `{ skipHistory: true }` keeps the resulting configuration off the undo stack.
   */
  configure(changes?: Record<string, { value: any }>, callback?: Function, options?: ParamateHistoryOptions): Promise<any> {
    const arg = readCallbackArg('configure', callback as Function | boolean | undefined, '{ skipHistory: true } as the third argument');
    if (options?.skipHistory ?? arg.skipHistory) ParamateUndo.suppressNext();
    // The SDK sends this map verbatim and never gathers the dirty params
    // itself, so the values staged with setParamValue(..., false) go in here,
    // under the changes named. A staged edit is an edit waiting for the next
    // configure, whichever call that is: a file input stages its token, and
    // the command a handler sends afterwards carries it.
    const payload = { ...pendingParamChanges(this.configurator), ...(changes ?? {}) };
    return this.configurator?.configure(payload, arg.callback);
  }

  /**
   * Trigger a configure cycle if any inputs have staged (pending) values.
   * Values are already set via setParamValue(..., false); this just
   * kicks off the server-side configure.
   */
  configurePending = () => {
    if (this.configurator) {
      configurePending(this.configurator);
    }
  }

  /**
   * Settles when the configure in flight, or the next one to start, has
   * finished, after its history entry is pushed. Rejects when that configure
   * fails. For a configure started by code that hands back no promise: the
   * Paramate GUI controls, or a linked file input clearing itself. A batch
   * that has to hold such a configure awaits this.
   */
  nextConfigure(): Promise<void> {
    return new Promise<void>((resolve, reject) => {
      const off = () => {
        window.removeEventListener('paramate-configurator-after-configure', done);
        window.removeEventListener('paramate-configurator-error', failed);
      };
      const done = () => { off(); resolve(); };
      const failed = (e: Event) => { off(); reject((e as CustomEvent).detail?.err ?? e); };
      window.addEventListener('paramate-configurator-after-configure', done);
      window.addEventListener('paramate-configurator-error', failed);
    });
  }

  /**
   * Upload a file (or files, for a `file[]` param) and set the param to the
   * resulting token(s). Queued until the configurator is ready.
   *
   * Also accepts Paramate's own options shape, `uploadFile({ file, fileId?,
   * progress?, success? })`, so a raw `configurator.uploadFile(...)` call can
   * move to this wrapper unchanged (and gain the queueing). That shape uploads
   * only — no param value is set, no configure — and resolves with the upload
   * response (carries `.token`), which `success` also receives.
   *
   * `file` may be an `AsFileHandle` in either shape — it's unwrapped to a real
   * `File` before it reaches Paramate.
   */
  uploadFile = (file: UploadFileInput | UploadFileInput[] | ConfiguratorUploadOptions, fileParamName = '', configure = false, label?: string) => new Promise<any>((resolve, reject) => {
    const opts = isUploadOptions(file) ? file : undefined;
    const files = opts ? opts.file : file as UploadFileInput | UploadFileInput[];
    // Paramate treats a falsy fileId as "allocate a new slot" (POST), so '' is
    // the right stand-in for an omitted one.
    const paramName = opts ? (opts.fileId ?? '') : fileParamName;
    if (!opts) ParamateUndo.setPendingLabel(label || `Upload ${(Array.isArray(files) ? files[0] : files)?.name}`);

    const uploadFn = async (configurator: any) => {
      try {
        const res = await this._uploadForParam(configurator, files, paramName, { configure, uploadOnly: !!opts, progress: opts?.progress });
        const out = opts ? res.uploadRes : res.parmRes;
        opts?.success?.(out);
        resolve(out);
      } catch (err) {
        reject(err);
      }
    }

    // If configurator is not initialized, queue the upload operation
    if (!this.configurator || !this.initialized) {
      const uploadOperation: QueuedUpload = {
        type: 'upload',
        file: files,
        fileParamName: paramName,
        execute: uploadFn
      };

      this.queueOperation(uploadOperation);
      return;
    }
    else return uploadFn(this.configurator);
  })

  /**
   * Convert a STEP/STP file server-side via Paramate's step-converter service
   * and return an upload token — the same shape uploadFile's uploadOnly mode
   * returns — without writing it to any param. STEP files aren't accepted by
   * Paramate's normal upload endpoint; this hits the dedicated converter
   * endpoint that produces a usable mesh token instead.
   */
  async convertStepFile(
    file: File,
    opts: { target?: number; linearDeflection?: number; angularDeflection?: number; progress?: Function } = {}
  ): Promise<{ token: string }> {
    if (!this.configurator) {
      console.warn('[as-configurator] convertStepFile called before initialization');
      return undefined as any;
    }
    const { target = 100000, linearDeflection = 0.32, angularDeflection = 1.0, progress } = opts;
    const url = `${this.configurator.APIHub}/services/step-converter/configSessions/${this.configurator.configSession}/files?target=${target}&ld=${linearDeflection}&ad=${angularDeflection}`;
    return this.configurator.doRequest({
      method: 'POST',
      url,
      contentType: '',
      responseType: 'json',
      data: file,
      progress,
    });
  }

  /**
   * Upload one or more files for a file param.
   *
   * trcad file params come in two shapes: a scalar `open file f` (value = one
   * upload token) and an array `open file f[]` (reported by Paramate as
   * { type: 'array', elementType: 'file' }, value = a token array). Several
   * files on a scalar param is an authoring mismatch — only the first is
   * uploaded, with a console warning (declare the param as `file f[]` to take
   * them all).
   *
   * Uploads run sequentially; each token is tracked via paramateUndoManager.ts's
   * trackUploadToken() (the first under the param key, the rest under
   * `<param>:<i>` so as-intrn-delete-file can find the whole set). An empty
   * fileParamName is a server-allocated anonymous slot — nothing to track it
   * under. With uploadOnly no param value is set. Returns the first token, all
   * tokens, the first upload's raw response, and the setParamValue result
   * (unless uploadOnly).
   */
  private async _uploadForParam(
    configurator: any,
    file: UploadFileInput | UploadFileInput[],
    fileParamName: string,
    opts: { configure: boolean; uploadOnly: boolean; progress?: Function },
  ): Promise<{ token: string; tokens: string[]; uploadRes: any; parmRes?: any }> {
    // Paramate needs a real File (it reads file.name) — unwrap AsFileHandles here
    // so no caller has to remember toFile().
    let files = (Array.isArray(file) ? file : [file])
      .map(f => f && typeof (f as any).toFile === 'function' ? (f as AsFileHandle).toFile() : f as File);
    if (!files.length) throw new Error(`[AS-CONFIGURATOR] upload for "${fileParamName}" called without a file`);

    const param = lookupParam(configurator.params, fileParamName);
    const isFileArray = param?.type === 'array' && param?.elementType === 'file';
    if (files.length > 1 && !isFileArray && !opts.uploadOnly) {
      console.warn(
        `[AS-CONFIGURATOR] "${fileParamName}" is a single-file param — uploading only the first of ` +
        `${files.length} files. Declare it as an array (\`open file <name>[]\`) to accept several.`
      );
      files = [files[0]];
    }

    const tokens: string[] = [];
    let firstUploadRes: any;
    // Busy for the length of the upload, as a configure is for its own. Later
    // clicks and undo wait, and the step selector locks, instead of racing a
    // file that is still going up or, for STEP, still converting.
    sendEv(window, 'as-app-busy-request', { detail: { key: 'upload', busy: true } });
    try {
      for (let i = 0; i < files.length; i++) {
        const fileId = fileParamName && i > 0 ? `${fileParamName}:${i}` : fileParamName;
        // Paramate's upload endpoint does not take STEP. The converter does,
        // and hands back a mesh token of the same shape.
        const uploadRes = /\.ste?p$/i.test(files[i].name ?? '')
          ? await this.convertStepFile(files[i], { progress: opts.progress })
          : await configurator.uploadFile({ file: files[i], fileId, progress: opts.progress });
        firstUploadRes ??= uploadRes;
        if (fileParamName) ParamateUndo.trackUploadToken(fileId, uploadRes.token);
        tokens.push(uploadRes.token);
      }
    } finally {
      sendEv(window, 'as-app-busy-request', { detail: { key: 'upload', busy: false } });
    }

    if (opts.uploadOnly) return { token: tokens[0], tokens, uploadRes: firstUploadRes };
    const value = isFileArray ? tokens : tokens[0];
    const parmRes = await configurator.setParamValue(fileParamName, value, opts.configure);
    return { token: tokens[0], tokens, uploadRes: firstUploadRes, parmRes };
  }

  // ============================================================================
  // EVENT COORDINATION
  // ============================================================================

  private _handleUploadFile = (event: CustomEvent<{
    file: UploadFileInput | UploadFileInput[],
    fileParamName: string,
    configure?: boolean,
    uploadOnly?: boolean,
    resolve: Function,
    reject: Function,
    progress?: Function,
    label?: string
  }>) => {
    const { file, fileParamName, configure = false, uploadOnly = false, resolve, reject, progress, label } = event.detail;
    ParamateUndo.setPendingLabel(label || `Upload ${Array.isArray(file) ? file[0]?.name : file.name}`);

    const run = async (configurator: any) => {
      try {
        const { token, tokens, parmRes } = await this._uploadForParam(
          configurator, file, fileParamName, { configure, uploadOnly, progress });
        if (uploadOnly) {
          // Upload-only mode: no param value set, no configure. tokens carries
          // the full set for multi-file consumers.
          resolve(token, tokens);
          return;
        }
        // Even if configure=false, publish so token-listeners can see it
        publishParams(configurator.params);
        resolve(token, parmRes, tokens);
      } catch (err) {
        reject(err);
      }
    };

    // If configurator is not initialized, queue the upload operation
    if (!this.configurator || !this.initialized) {
      const uploadOperation: QueuedUpload = {
        type: 'upload',
        file,
        fileParamName,
        execute: run,
      };

      this.queueOperation(uploadOperation);
      return;
    }

    run(this.configurator);
  }

  private _handleDeleteFile = (event: CustomEvent<{ fileParamName: string }>) => {
    const { fileParamName } = event.detail;
    if (!this.configurator) return;
    // Multi-file uploads track their extra tokens under `<param>:<i>` — delete
    // the whole set along with the primary key.
    ParamateUndo.releaseTokensForParam(fileParamName);
  }

  handleSystemInfoReq = (event) => {
    if (!this.initialized) {
      this._waitingForSysInfo.push(event.detail.requester);
    } else {
      this.sendSystemInfo(event.detail.requester);
    }
  }

  // ============================================================================
  // QUEUE MANAGEMENT
  // ============================================================================

  queueOperation = (operation: QueuedOperation) => {
    // Add to queue
    this._operationQueue.push(operation);

    // If this is the first queued operation, show a spinner
    if (this._operationQueue.length === 1 && !this._spinner) {
      this._spinner = document.createElement('as-spinner');
      document.body.appendChild(this._spinner);
    }
  }

  processOperationQueue = async () => {
    if (this._operationQueue.length === 0) return;

    // Remove spinner if it exists
    if (this._spinner) {
      this._spinner.remove();
      this._spinner = null;
    }

    // Make a copy of the queue and clear it before processing
    // This prevents new operations added during processing from being lost
    const queueToProcess = [...this._operationQueue];
    this._operationQueue = [];

    // Process each queued operation
    for (const operation of queueToProcess) {
      try {
        await operation.execute(this.configurator);
      } catch (err) {
        // Error handling for failed operations
      }
    }
  }

  // ============================================================================
  // SYSTEM INFO
  // ============================================================================

  getSystemInfo = () => {
    const info = {
      sessionId: this.configurator.configSession,
    }
    return info;
  }

  sendSystemInfo = (requester) => {
    const info = this.getSystemInfo();
    sendEv(requester, 'as-intrn-system-info', { bubbles: false, detail: { info } });
  }

  sendSystemInfoToQueue = () => {
    this._waitingForSysInfo.forEach(requester => {
      this.sendSystemInfo(requester);
    });
  }

  // ============================================================================
  // UNDO / REDO
  // ============================================================================
  // Thin pass-throughs to paramateUndoManager.ts, which owns the actual
  // undo/redo state machine and the upload-token lifecycle it drives.

  get canUndo(): boolean { return History.canUndo(); }
  get canRedo(): boolean { return History.canRedo(); }

  undo(): void { ParamateUndo.undo(); }
  redo(): void { ParamateUndo.redo(); }

  /** @param skipRemoteDelete When true, skip the per-token deleteFile() cleanup
   *  (still clears local bookkeeping) — for callers abandoning the WHOLE old
   *  session for a fresh one (_reinitSession), where chasing individual file
   *  tokens on the way out is pointless (and, if it raced the session swap,
   *  used to be an extra 401 in the console). */
  clearHistory(skipRemoteDelete = false): void {
    ParamateUndo.clearHistory(skipRemoteDelete);
  }

  private _resetHistory(): void { this.clearHistory(); }

  private _handleProjectLoaded = () => {
    ParamateUndo.clearSuppress(); // ensure no stale suppress from a prior new-session reset
    this._resetHistory();
    if (this.configurator?.params) {
      this._syncParamValuesToInputs(this.configurator);
      publishParams(this.configurator.params);
    }
  };
}

// ============================================================================
// REGISTRATION
// ============================================================================

AsConfigurator.define();

// Lets wcQry('as-configurator') AND plain document.querySelector('as-configurator')
// resolve typed as AsConfigurator, the same as passing the class.
declare global {
  interface HTMLElementTagNameMap {
    'as-configurator': AsConfigurator;
  }
}
